import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi } from './api';
import { Button } from '../components/ui/button';
import { Sparkles, AlertCircle, Lock } from 'lucide-react';
import { logger } from '../lib/logger';

/**
 * UsageNudge — a contextual, subtle upgrade prompt shown on free-tier feature
 * pages (AI Coach, Pre-Game, Journal) when the user is approaching or has hit
 * their daily cap. Re-fetches on `refreshKey` change so it updates after each
 * action.
 *
 * Tiered messaging by % used:
 *   <60%   → hidden (don't annoy active users)
 *   60-79% → soft heads-up (teal)
 *   80-99% → urgent warning + Upgrade CTA (amber)
 *   100%   → hard wall + prominent Upgrade CTA (rose)
 *
 * Paid users (pro/premium) never see this — the API returns very high limits
 * (50-9999) so the % threshold won't trigger in normal use.
 */
const KIND_LABELS = {
  coach: 'AI Coach messages',
  pregame: 'pre-game plans',
  journal: 'journal entries',
};

export default function UsageNudge({ kind, refreshKey }) {
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    aiApi
      .usage()
      .then((u) => {
        if (!cancelled) setUsage(u);
      })
      .catch((err) => logger.error('UsageNudge: failed to load usage', err));
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (!usage) return null;
  const u = usage[kind];
  if (!u || u.limit >= 1000) return null; // paid tier — effectively unlimited

  const pct = u.limit > 0 ? (u.used / u.limit) * 100 : 0;
  if (pct < 60) return null; // not yet worth nudging

  // ----- Visual variants ----------------------------------------------------
  let variant;
  if (pct >= 100) {
    variant = {
      bg: 'bg-rose-50 border-rose-200',
      iconWrap: 'bg-rose-100 text-rose-700',
      icon: <Lock className="h-4 w-4" />,
      labelClass: 'text-rose-900',
      headline: `You've hit today's free ${KIND_LABELS[kind]} limit`,
      sub: `Upgrade to Apex AI for 10× the daily usage and unlock the full video library.`,
      ctaClass: 'bg-rose-700 hover:bg-rose-800 text-white',
      ctaLabel: 'Upgrade now',
    };
  } else if (pct >= 80) {
    variant = {
      bg: 'bg-amber-50 border-amber-200',
      iconWrap: 'bg-amber-100 text-amber-700',
      icon: <AlertCircle className="h-4 w-4" />,
      labelClass: 'text-amber-900',
      headline: `Only ${u.remaining} ${KIND_LABELS[kind]} left today`,
      sub: `Resets at midnight UTC. Upgrade to never hit a daily cap.`,
      ctaClass: 'bg-amber-700 hover:bg-amber-800 text-white',
      ctaLabel: 'See plans',
    };
  } else {
    variant = {
      bg: 'bg-[#004C54]/5 border-[#004C54]/20',
      iconWrap: 'bg-[#004C54]/10 text-[#004C54]',
      icon: <Sparkles className="h-4 w-4" />,
      labelClass: 'text-[#004C54]',
      headline: `${u.remaining} of ${u.limit} ${KIND_LABELS[kind]} left today`,
      sub: `Training a lot? Apex AI gives you 10× the daily usage.`,
      ctaClass:
        'bg-white border border-[#004C54]/30 text-[#004C54] hover:bg-[#004C54] hover:text-white',
      ctaLabel: 'Upgrade',
    };
  }

  // Compute the nudge-level for analytics + testing without nested ternaries.
  const nudgeLevel = (() => {
    if (pct >= 100) return 'wall';
    if (pct >= 80) return 'urgent';
    return 'soft';
  })();

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-3 sm:p-4 ${variant.bg}`}
      data-testid={`usage-nudge-${kind}`}
      data-nudge-level={nudgeLevel}
    >
      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${variant.iconWrap}`}>
        {variant.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${variant.labelClass}`}>{variant.headline}</div>
        <div className="text-xs text-foreground/70 mt-0.5">{variant.sub}</div>
      </div>
      <Button
        size="sm"
        onClick={() => navigate('/app/pricing')}
        className={`shrink-0 font-semibold ${variant.ctaClass}`}
        data-testid={`usage-nudge-cta-${kind}`}
      >
        {variant.ctaLabel}
      </Button>
    </div>
  );
}
